import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Chip,
  Typography,
  Button,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Assignment as TaskIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  ContentCopy as CloneIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Loop as LoopIcon,
} from '@mui/icons-material';
import {
  List,
  Datagrid,
  TextField,
  FunctionField,
  useRefresh,
  useDataProvider,
} from 'react-admin';
import { isVirtualTask } from '../../../utils/taskUtils';
import type { TaskFilters } from '../types';
import type { Workflow } from '../../../types/workflow.types';
import { TaskDetailsDialog } from '../../../components/TaskDetailsDialog';
import { TaskDialog } from '../../../components/TaskDialog';

// Helper function to get priority color
const getPriorityColor = (priority: string) => {
  switch (priority?.toLowerCase()) {
    case 'urgent':
      return { backgroundColor: '#dc2626', color: 'white' }; // Red
    case 'high':
      return { backgroundColor: '#ea580c', color: 'white' }; // Orange
    case 'medium':
      return { backgroundColor: '#2563eb', color: 'white' }; // Blue
    case 'low':
      return { backgroundColor: '#10b981', color: 'white' }; // Green
    default:
      return { backgroundColor: '#9ca3af', color: 'white' }; // Gray
  }
};

// Task Actions Component
const TaskActions = ({
  record,
  onEdit,
  onClone,
  onView
}: {
  record: any;
  onEdit: (task: any) => void;
  onClone: (task: any) => void;
  onView: (task: any) => void;
}) => {
  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit(record);
  };

  const handleClone = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClone(record);
  };

  const handleView = (e: React.MouseEvent) => {
    e.stopPropagation();
    onView(record);
  };

  return (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
      <Tooltip title="Edit task">
        <IconButton size="small" onClick={handleEdit}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Clone task">
        <IconButton size="small" onClick={handleClone}>
          <CloneIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

interface TaskListViewProps {
  filters: TaskFilters;
  selectedWorkflow?: Workflow;
  onClearFilters?: () => void;
}

export const TaskListView: React.FC<TaskListViewProps> = ({ filters, selectedWorkflow, onClearFilters }) => {
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [loadedWorkflow, setLoadedWorkflow] = useState<any>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [cloneTask, setCloneTask] = useState<any>(null);

  // Build react-admin filters from our custom filters
  const raFilters = useMemo(() => {
    const result: any = {};

    if (filters.search) {
      result.q = filters.search;
    }
    if (filters.workflowId) {
      result.workflowId = filters.workflowId;
    }
    if (filters.customerId) {
      result.customerId = filters.customerId;
    }
    if (filters.zoneId) {
      result.zoneId = filters.zoneId;
    }
    if (filters.categoryId) {
      result.categoryId = filters.categoryId;
    }
    if (filters.statusId) {
      result.statusId = filters.statusId;
    }
    if (filters.assigneeId) {
      result.assigneeId = filters.assigneeId;
    }
    if (filters.priority) {
      result.priority = filters.priority;
    }
    if (filters.dateRange?.start) {
      result.startDate = filters.dateRange.start.toISOString();
    }
    if (filters.dateRange?.end) {
      result.endDate = filters.dateRange.end.toISOString();
    }

    return result;
  }, [filters]);

  const handleRowClick = async (id: string, data: any): Promise<false> => {
    try {
      // Fetch the full task details
      const { data: fullTask } = await dataProvider.getOne('tasks', { id });
      setSelectedTask(fullTask);

      // Fetch the workflow for this task if not already provided
      const workflowToUse = selectedWorkflow || loadedWorkflow;
      if (!workflowToUse && fullTask.workflowId) {
        try {
          const { data: workflow } = await dataProvider.getOne('workflows', { id: fullTask.workflowId });
          setLoadedWorkflow(workflow);
        } catch (error) {
          console.error('Failed to fetch workflow:', error);
        }
      }
    } catch (error) {
      console.error('Failed to fetch task details:', error);
      // Fall back to using the row data if fetch fails
      setSelectedTask(data);
    }
    setEditDialogOpen(true);
    return false; // Prevent navigation
  };

  const handleEdit = async (task: any) => {
    try {
      // Fetch the full task details
      const { data: fullTask } = await dataProvider.getOne('tasks', { id: task.id });
      setSelectedTask(fullTask);
      setEditDialogOpen(true);
    } catch (error) {
      console.error('Failed to fetch task details:', error);
      setSelectedTask(task);
      setEditDialogOpen(true);
    }
  };

  const handleClone = async (task: any) => {
    try {
      // Get the first status from the workflow
      let firstStatusId = null;
      if (task.workflowId) {
        const { data: workflow } = await dataProvider.getOne('workflows', { id: task.workflowId });
        if (workflow.statuses && workflow.statuses.length > 0) {
          // Statuses are ordered by orderIndex, so the first one is the first status
          firstStatusId = workflow.statuses[0].id;
        }
      }

      // Create a cloned task with same data but remove ID and reset some fields
      const clonedTask = {
        ...task,
        id: undefined, // Remove ID so it creates a new task
        statusId: firstStatusId || task.statusId, // Use first workflow status or keep existing
        createdAt: undefined,
        updatedAt: undefined,
        // Keep all other fields like description, priority, assignee, title, etc.
      };
      setCloneTask(clonedTask);
      setCreateDialogOpen(true);
    } catch (error) {
      console.error('Failed to fetch workflow for cloning:', error);
      // Fall back to original behavior if workflow fetch fails
      const clonedTask = {
        ...task,
        id: undefined,
        statusId: task.statusId, // Keep existing status as fallback
        createdAt: undefined,
        updatedAt: undefined,
      };
      setCloneTask(clonedTask);
      setCreateDialogOpen(true);
    }
  };

  const handleView = async (task: any) => {
    try {
      // Fetch the full task details
      const { data: fullTask } = await dataProvider.getOne('tasks', { id: task.id });
      setSelectedTask(fullTask);
      setViewDialogOpen(true);
    } catch (error) {
      console.error('Failed to fetch task details:', error);
      setSelectedTask(task);
      setViewDialogOpen(true);
    }
  };

  const handleDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedTask(null);
    setCloneTask(null);
    setLoadedWorkflow(null);
  };

  const handleSwitchTask = (newTask: any) => {
    setEditDialogOpen(false);
    setTimeout(() => {
      setSelectedTask(newTask);
      setEditDialogOpen(true);
    }, 300);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Paper sx={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', p: 2 }}>
        <List
          resource="tasks"
          filter={raFilters}
          perPage={25}
          sort={{ field: 'scheduledDate', order: 'ASC' }}
          empty={
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1,
                minHeight: '400px',
                py: 8,
                px: 3,
              }}
            >
              <TaskIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h5" gutterBottom color="text.secondary">
                No tasks found
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center', maxWidth: 400 }}>
                {filters.search || filters.statusId?.length || filters.assigneeId || filters.customerId || filters.categoryId || filters.priority?.length
                  ? 'Try adjusting your filters to find tasks'
                  : selectedWorkflow
                    ? `No tasks in the ${selectedWorkflow.name} workflow yet`
                    : 'Select a workflow to view tasks'
                }
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setCreateDialogOpen(true)}
                >
                  Create First Task
                </Button>
                {(filters.search || filters.statusId?.length || filters.assigneeId || filters.customerId || filters.categoryId || filters.priority?.length) && (
                  <Button
                    variant="outlined"
                    startIcon={<FilterIcon />}
                    onClick={() => {
                      if (onClearFilters) {
                        onClearFilters();
                      }
                      refresh();
                    }}
                  >
                    Clear Filters
                  </Button>
                )}
              </Box>
            </Box>
          }
          actions={false}
          hasCreate={false}
        >
          <Datagrid
            bulkActionButtons={false}
            rowClick="show"
            sx={{
              '& .RaDatagrid-headerCell': {
                fontWeight: 600,
              },
            }}
          >
            <TextField source="title" label="Task Name" />
            <FunctionField
              source="customer"
              label="Customer"
              render={(record: any) =>
                record.customer?.displayName ||
                record.customer?.company ||
                '-'
              }
            />
            <FunctionField
              source="customerLocation"
              label="Location"
              render={(record: any) => {
                const loc = record?.customerLocation?.location;
                if (!loc) { return '-'; }
                const parts: string[] = [];
                if (loc.name) { parts.push(loc.name); }
                if (loc.streetLine1 && loc.streetLine1.toString().trim() !== '') {
                  parts.push(loc.streetLine1.toString());
                }
                if (loc.province && loc.province.toString().trim() !== '') {
                  parts.push(loc.province.toString());
                }
                if (loc.postalCode && loc.postalCode.toString().trim() !== '') {
                  parts.push(loc.postalCode.toString());
                }

                return parts.length > 0 ? parts.join(', ') : '-';
              }}
            />
            <FunctionField
              source="category"
              label="Category"
              render={(record: any) =>
                record.category?.name || '-'
              }
            />
            <FunctionField
              source="workflowStatus"
              label="Status"
              render={(record: any) => {
                // Try to get status from workflowStatus relationship or find it in workflow
                const status = record.workflowStatus ||
                  selectedWorkflow?.statuses?.find(s => s.id === record.statusId);

                if (!status) {
                  return <Chip label="No Status" size="small" />;
                }

                return (
                  <Chip
                    label={status.name}
                    size="small"
                    sx={{
                      backgroundColor: status.color,
                      color: 'white',
                      fontWeight: 500,
                    }}
                  />
                );
              }}
            />
            <FunctionField
              source="scheduledDate"
              label="Scheduled"
              render={(record: any) => {
                const sd = record?.scheduledDate;
                if (!sd) { return '-'; }
                const scheduledDate = new Date(sd);
                if (isNaN(scheduledDate.getTime())) { return '-'; }

                const yyyy = scheduledDate.getFullYear();
                const mm = String(scheduledDate.getMonth() + 1).padStart(2, '0');
                const dd = String(scheduledDate.getDate()).padStart(2, '0');
                const datePart = `${yyyy}-${mm}-${dd}`;

                const timePart = scheduledDate.toLocaleTimeString(undefined, {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                });

                const rawDuration = record?.estimatedHours;
                const durationPart = rawDuration !== null
                  ? `, ${Number.isInteger(rawDuration) ? rawDuration : parseFloat(rawDuration.toFixed(2))}h`
                  : '';

                return (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" component="span">
                        {datePart}, {timePart}{durationPart}
                      </Typography>
                      {isVirtualTask(record) && (
                        <Tooltip title="Recurring Task Instance">
                          <LoopIcon fontSize="inherit" color="action" sx={{ fontSize: '0.9rem' }} />
                        </Tooltip>
                      )}
                    </Box>
                    {(!isVirtualTask(record) && record.recurrenceFrequency) && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <LoopIcon fontSize="inherit" color="primary" sx={{ fontSize: '0.8rem' }} />
                        <Typography variant="caption" color="text.secondary">
                          {record.recurrenceFrequency}
                          {record.recurrenceEndDate && `, Ends ${new Date(record.recurrenceEndDate).toLocaleDateString()}`}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              }}
            />
            <FunctionField
              source="assignee"
              label="Assigned"
              render={(record: any) => {
                if (record.assignees && record.assignees.length > 0) {
                  return record.assignees
                    .map((a: any) => a.name || a.email || 'Unknown')
                    .join(', ');
                }
                return record.assignee?.name ||
                  record.assignee?.email ||
                  'Unassigned';
              }}
            />
            <FunctionField
              source="priority"
              label="Priority"
              render={(record: any) => {
                if (!record.priority) return '-';
                return (
                  <Chip
                    label={record.priority}
                    size="small"
                    sx={{
                      ...getPriorityColor(record.priority),
                      fontWeight: 500,
                    }}
                  />
                );
              }}
            />
            <FunctionField
              source="id"
              label="Actions"
              render={(record: any) => (
                <TaskActions
                  record={record}
                  onEdit={handleEdit}
                  onClone={handleClone}
                  onView={handleView}
                />
              )}
            />
          </Datagrid>
        </List>
      </Paper>
      {/* Task Dialog (Edit & Create) */}
      <TaskDialog
        open={editDialogOpen || createDialogOpen}
        onClose={() => {
          handleDialogClose();
          setCreateDialogOpen(false);
        }}
        task={editDialogOpen ? selectedTask : undefined}
        initialData={cloneTask}
        workflowId={selectedWorkflow?.id}
        onSuccess={() => {
          refresh();
          handleDialogClose();
          setCreateDialogOpen(false);
        }}
        onDelete={() => {
          refresh();
          handleDialogClose();
        }}
        onSwitchTask={handleSwitchTask}
      />

      {/* View Task Dialog */}
      <TaskDetailsDialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false);
          setSelectedTask(null);
        }}
        task={selectedTask}
        onClone={handleClone}
      />
    </Box>
  );
};